import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AlertCircle, Pause, RefreshCw, Rss, Trash2 } from 'lucide-react';

import { App, Subscription } from '../types/api';
import ConfirmDialog from './ConfirmDialog';
import { CredentialField } from './CredentialField';
import { DialogSectionRail, type DialogSection } from './DialogSectionRail';
import { AppSecretField } from './AppSecretField';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { SaveBar } from './SaveBar';
import { StatusBadge } from './StatusBadge';
import { trimmedText, useRecordChanges, type RecordComparators } from '../hooks/useRecordChanges';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button, DisabledReasonText } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { RecordDialogHeader } from './RecordDialogHeader';
import { Field } from './Field';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Toggle } from './ui/toggle';
import { PropertyList, PropertyRow, SectionHeading } from './PropertyList';
import { DateCell } from './Timestamp';
import { Skeleton } from './ui/skeleton';
import { apiService } from '../services/api';
import { cn } from '../lib/utils';
import { formatDateTime, formatDays, formatDuration, formatNumber, EMPTY_VALUE } from '../lib/formatters';
import { appClientSubscriptionBlocker, humanizeError } from '../lib/errors';
import { validateAppName } from '../lib/validation';
import { describeCoverageReason, describeSubscriptionError } from '../lib/subscriptionErrors';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/** The only editable field; whitespace around it is not a change. */
const NAME_COMPARATORS: RecordComparators<{ name: string }> = { name: trimmedText };

/** Rung 3 reason: nothing else on screen says a publisher credential can't go on its own. */
export const PUBLISHER_DELETE_REASON = "A template's publisher credential is deleted with its template.";

interface AppDetailsModalProps {
  app: App | null;
  organizationId: string;
  accessToken: string;
  onClose: () => void;
  onDelete: () => Promise<void>;
  /**
   * Whether to offer the per-subscription delete control.
   *
   * A prop rather than a `usePermissions` call inside: this component takes
   * everything it needs from its caller, which is what lets it be rendered in a
   * test without the whole provider tree standing behind it.
   */
  canDeleteSubscriptions?: boolean;
  /**
   * Whether to offer the client secret reveal. GET /apps/{id}/credentials
   * checks app_clients:write, so a reader without it is never shown a control
   * that can only answer 403. The client ID stays visible to everyone.
   */
  canReadSecret?: boolean;
  /** Whether to offer Delete app client — DELETE /apps/{id} checks app_clients:delete. */
  canDelete?: boolean;
  /**
   * Which pages an uncovered event type may link to — where the viewer can
   * actually fix it. Props, for the same reason as `canDeleteSubscriptions`;
   * absent means no links, which also keeps the component renderable without
   * a router.
   */
  coverageLinks?: { lenses?: boolean; sensors?: boolean };
  /**
   * Renames the app. Resolves once the caller has refreshed its own copy.
   *
   * Absent for an app that cannot be renamed at all — a template's publisher
   * credential, whose lifetime and name belong to its template.
   */
  onRename?: (name: string) => Promise<void>;
}


type AppSection = 'details' | 'subscriptions';

const AppDetailsModal: React.FC<AppDetailsModalProps> = ({
  app, organizationId, accessToken, onClose, onDelete, canDeleteSubscriptions = false,
  canReadSecret = false, canDelete = false, coverageLinks = {}, onRename,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  /**
   * The count from a delete the API refused because subscriptions still exist
   * (one created after this dialog loaded). It disables Delete and sends the
   * reader to the Subscriptions section when the confirmation closes.
   */
  const [raceBlockerCount, setRaceBlockerCount] = useState<number | null>(null);
  /** The subscription a confirm dialog is currently open for, if any. */
  const [pendingSubscriptionDelete, setPendingSubscriptionDelete] = useState<Subscription | null>(null);

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [subscriptionsError, setSubscriptionsError] = useState<unknown>(null);
  const [section, setSection] = useState<AppSection>('details');

  /*
   * The name, edited in place.
   *
   * It used to be a dialog of its own, reached from a row pencil — so the one
   * field on this object anybody changes was the one field this dialog could
   * not show. `baseline` is what the server last confirmed, and Save wakes on a
   * difference from it rather than on the field being touched, the same rule
   * the template workbench uses.
   */
  const [name, setName] = useState('');
  const [nameBaseline, setNameBaseline] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  /*
   * Keyed on the app's ID, deliberately not on the object: the page hands back
   * a new one after every refresh, and re-running this on that would throw away
   * what the reader had typed.
   */
  const appId = app?.id ?? null;
  useEffect(() => {
    if (!app) return;
    setName(app.name);
    setNameBaseline(app.name);
    setNameError(null);
    setIsSavingName(false);
    setRaceBlockerCount(null);
    setSection('details');
    setAutoRefreshPaused(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const REFRESH_INTERVAL = 5; // seconds
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  // WCAG 2.2.2: content that refreshes itself offers a way to stop it.
  const [autoRefreshPaused, setAutoRefreshPaused] = useState(false);
  const refreshHintId = React.useId();
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  // The countdown is also kept in a ref so the ticker can decide whether this
  // tick is the one that refetches without reading it out of a state updater —
  // see the ticker effect below.
  const secondsLeftRef = useRef(REFRESH_INTERVAL);

  // Ordering guard: the 5s ticker and manual refresh can interleave, and a
  // slow older response must never overwrite a newer one. Only the latest
  // request (by monotonic id) may commit state.
  const fetchRequestIdRef = useRef(0);

  const fetchSubscriptions = useCallback(async () => {
    if (!app) return;
    const requestId = ++fetchRequestIdRef.current;
    setSubscriptionsError(null);
    try {
      const data = await apiService.getAppClientSubscriptions(organizationId, app.id, accessToken);
      if (fetchRequestIdRef.current !== requestId) return;
      setSubscriptions(data);
    } catch (err: unknown) {
      if (fetchRequestIdRef.current !== requestId) return;
      console.error('Failed to load subscriptions:', err);
      setSubscriptionsError(err);
    } finally {
      if (fetchRequestIdRef.current === requestId) setIsInitialLoad(false);
    }
  }, [organizationId, app, accessToken]);

  const resetCountdown = useCallback(() => {
    secondsLeftRef.current = REFRESH_INTERVAL;
    setCountdown(REFRESH_INTERVAL);
  }, []);

  // Reaching the section: skeletons, one fetch, a fresh countdown. Split from
  // the ticker below so pausing and resuming neither refetches nor flashes the
  // skeleton.
  useEffect(() => {
    if (section !== 'subscriptions') return;
    setIsInitialLoad(true);
    fetchSubscriptions();
    resetCountdown();
  }, [section, fetchSubscriptions, resetCountdown]);

  // Auto-refresh ticker: only runs while the subscriptions section is showing
  // and the reader has not paused it.
  useEffect(() => {
    if (section !== 'subscriptions' || autoRefreshPaused) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    // The tick decides in the interval callback, not inside a state updater.
    // A function passed to a setter must be pure — it computes the next value
    // and nothing else. React 18 happens to invoke it once per tick, so calling
    // fetchSubscriptions() from in there fired exactly one request; React 19's
    // strict mode double-invokes updaters and would fire two.
    countdownRef.current = setInterval(() => {
      const next = secondsLeftRef.current - 1;
      if (next <= 0) {
        secondsLeftRef.current = REFRESH_INTERVAL;
        setCountdown(REFRESH_INTERVAL);
        fetchSubscriptions();
        return;
      }
      secondsLeftRef.current = next;
      setCountdown(next);
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [section, autoRefreshPaused, fetchSubscriptions]);

  const nameBaselineForm = useMemo(() => ({ name: nameBaseline }), [nameBaseline]);
  const nameForm = useMemo(() => ({ name }), [name]);
  const { changeCount: nameChangeCount, isDirty: isNameDirty } = useRecordChanges(
    nameBaselineForm,
    nameForm,
    NAME_COMPARATORS,
  );

  const discardName = useCallback(() => {
    setName(nameBaseline);
    setNameError(null);
  }, [nameBaseline]);

  // Every close path — X, Escape, outside click — routes through one guard,
  // so a pending rename is never dropped silently.
  const guard = useDiscardGuard({
    isDirty: Boolean(onRename) && isNameDirty,
    isBusy: isSavingName,
    onClose,
    onDiscard: discardName,
    recordNoun: app ? `app client "${app.name}"` : 'this app client',
  });

  if (!app) return null;
  const isTemplatePublisher = app.clientKind === 'template_publisher';

  /*
   * The count in the rail comes from the list payload until the section has
   * been opened, and from the live list after — so it is right before anyone
   * clicks, and stays right while the 5-second ticker runs. `subscriptionCount`
   * is optional on purpose: My Apps fills it in on a second request so a slow
   * count never delays the page, and `null` renders nothing rather than a `0`
   * that would read as "none".
   */
  const subscriptionCount = section === 'subscriptions' && !isInitialLoad
    ? subscriptions.length
    : app.subscriptionCount ?? null;

  /*
   * Delete is refused by the API while subscriptions exist, so say so beside
   * the button instead of letting the reader type the name and fail. An
   * unknown count (null) never blocks; the refused-delete path still covers it.
   * The sentence is the Button's disabledReason (focusable, described) and is
   * repeated as visible text beside it, because a tooltip never reaches touch.
   */
  const blockingCount = Math.max(subscriptionCount ?? 0, raceBlockerCount ?? 0);
  const deleteBlockedReason = isTemplatePublisher
    ? PUBLISHER_DELETE_REASON
    : blockingCount > 0
      ? `Delete this app client's ${formatNumber(blockingCount)} subscription${blockingCount === 1 ? '' : 's'} first.`
      : undefined;

  const sections: Array<DialogSection<AppSection>> = [
    { value: 'details', label: 'Details' },
    { value: 'subscriptions', label: 'Subscriptions', count: subscriptionCount },
  ];

  const saveName = async () => {
    if (!onRename || !isNameDirty || isSavingName) return;
    const normalized = name.trim();
    const invalid = validateAppName(normalized);
    if (invalid) {
      setNameError(invalid);
      return;
    }
    setIsSavingName(true);
    setNameError(null);
    try {
      await onRename(normalized);
      setNameBaseline(normalized);
      setName(normalized);
    } catch (err: unknown) {
      console.error('Failed to rename the app:', err);
      setNameError(humanizeError(err, "Couldn't rename the app"));
    } finally {
      setIsSavingName(false);
    }
  };

  const handleRefreshClick = () => {
    fetchSubscriptions();
    resetCountdown();
  };

  return (
    <>
      {/* Locked only for the rename, whose result shows inline. The
          subscription auto-refresh is a background read and never locks. */}
      <Dialog
        open={Boolean(app)}
        pending={isSavingName}
        onOpenChange={guard.onOpenChange}
      >
        <DialogContent size="xl" frame className="h-[660px]">
          {/* Identity. Fixed, so it stays put when the section changes. */}
          <RecordDialogHeader
            title={app.name}
            id={app.clientId}
            srDescription="Client ID"
            badges={
              <>
                <StatusBadge status={isTemplatePublisher ? 'template_publisher' : 'app'} />
                {app.detailsUnavailable ? (
                  <StatusBadge status="degraded" label="Details unavailable" />
                ) : null}
              </>
            }
          />

          <div
            className={cn(
              'grid min-h-0 flex-1 grid-cols-1 overflow-hidden',
              sections.length > 1 && 'sm:grid-cols-[13rem_1fr]',
            )}
          >
            <DialogSectionRail
              label="App client sections"
              sections={sections}
              value={section}
              onChange={setSection}
            />

            {/* The content column: scroll area above, save bar pinned below. */}
            <div className="flex min-h-0 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {section === 'details' ? (
              <>
                <div className="space-y-6">
                <section className="space-y-3">
                  <SectionHeading>Basic information</SectionHeading>
                  <div className="space-y-3">
                    {/*
                      An input where the name can change, a value where it
                      cannot — a template's publisher credential is named by its
                      template and the API 409s a rename.
                    */}
                    {onRename ? (
                      <Field label="App client name" error={nameError} required>
                        {(control) => (
                          <Input
                            {...control}
                            value={name}
                            autoComplete="off"
                            disabled={isSavingName}
                            onChange={(event) => {
                              setName(event.target.value);
                              setNameError(null);
                            }}
                          />
                        )}
                      </Field>
                    ) : null}
                    <CredentialField label="Client ID" value={app.clientId} />
                    {canReadSecret ? (
                      <AppSecretField
                        appId={app.id}
                        accessToken={accessToken}
                        initialSecret={app.clientSecret || undefined}
                        className="min-w-0"
                      />
                    ) : null}
                  </div>
                </section>

                <Separator />

                <section className="space-y-3">
                  <SectionHeading>Token settings</SectionHeading>
                  {/* The one 9.5rem label column: long labels wrap, never widen it. */}
                  <PropertyList>
                    <PropertyRow label="Authentication flow session duration">
                      {app.authFlowSessionDuration != null ? formatDuration(app.authFlowSessionDuration) : null}
                    </PropertyRow>
                    <PropertyRow label="Refresh token expiration">
                      {app.refreshTokenExpiration != null ? formatDays(app.refreshTokenExpiration) : null}
                    </PropertyRow>
                    <PropertyRow label="Access token expiration">
                      {app.accessTokenExpiration != null ? formatDuration(app.accessTokenExpiration) : null}
                    </PropertyRow>
                    <PropertyRow label="ID token expiration">
                      {app.idTokenExpiration != null ? formatDuration(app.idTokenExpiration) : null}
                    </PropertyRow>
                  </PropertyList>
                </section>

                <Separator />

                <section className="space-y-3">
                  <SectionHeading>Advanced authentication settings</SectionHeading>
                  <PropertyList>
                    <PropertyRow label="Enable token revocation">
                      {app.enableTokenRevocation != null ? (app.enableTokenRevocation ? 'Enabled' : 'Disabled') : null}
                    </PropertyRow>
                  </PropertyList>
                </section>

                <Separator />

                <section className="space-y-3">
                  <SectionHeading>Timestamps</SectionHeading>
                  <PropertyList>
                    <PropertyRow label="Created"><DateCell value={app.createdAt} precision="datetime" /></PropertyRow>
                    <PropertyRow label="Updated"><DateCell value={app.updatedAt || app.createdAt} precision="datetime" /></PropertyRow>
                  </PropertyList>
                </section>
              </div>

              {/*
                Destructive, and it belongs at the bottom of the section that
                describes the thing it destroys — not in a footer riding along
                over the subscriptions list, where it would sit beside rows it
                does not act on.
              */}
              {canDelete ? (
                // Left-aligned danger row (DESIGN_SYSTEM.md §5 dialog anatomy):
                // the bottom-right corner is never a lone red button.
                <div className="mt-6 flex flex-wrap items-center justify-start gap-3 border-t pt-4">
                  <Button
                    type="button"
                    variant="destructive"
                    disabledReason={deleteBlockedReason}
                    onClick={() => guard.confirmDiscard(() => setDeleteDialogOpen(true))}
                  >
                    <Trash2 aria-hidden="true" />
                    Delete app client
                  </Button>
                  {deleteBlockedReason ? (
                    <DisabledReasonText>{deleteBlockedReason}</DisabledReasonText>
                  ) : null}
                  {!isTemplatePublisher && blockingCount > 0 ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => setSection('subscriptions')}>
                      Go to Subscriptions
                    </Button>
                  ) : null}
                </div>
              ) : null}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    Subscriptions created using this app client's credentials.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* A ticker is never announced: aria-hidden, and the Refresh
                        button's description says the list refreshes itself
                        (DESIGN_SYSTEM.md §8.4). */}
                    <span aria-hidden="true" className="text-xs tabular-nums text-muted-foreground">
                      {autoRefreshPaused ? 'Auto-refresh paused' : `Refreshing in ${countdown}s`}
                    </span>
                    <Toggle
                      variant="outline"
                      size="sm"
                      pressed={autoRefreshPaused}
                      onPressedChange={(pressed) => {
                        setAutoRefreshPaused(pressed);
                        if (!pressed) resetCountdown();
                      }}
                    >
                      <Pause aria-hidden="true" />
                      Pause auto-refresh
                    </Toggle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefreshClick}
                      aria-describedby={refreshHintId}
                    >
                      <RefreshCw aria-hidden="true" />
                      Refresh
                    </Button>
                    <span id={refreshHintId} className="sr-only">
                      {autoRefreshPaused
                        ? 'Automatic refresh is paused.'
                        : 'Subscriptions refresh automatically every 5 seconds.'}
                    </span>
                  </div>
                </div>

                {isInitialLoad ? (
                  <div className="space-y-2" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="rounded-md border bg-card p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1 space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-56" />
                          </div>
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <div className="mt-3 flex gap-4">
                          <Skeleton className="h-3 w-32" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : subscriptionsError != null && subscriptions.length === 0 ? (
                  <ErrorState
                    title="Couldn't load subscriptions"
                    error={subscriptionsError}
                    compact
                    onRetry={handleRefreshClick}
                  />
                ) : (
                  <>
                    {subscriptionsError != null ? (
                      <Alert variant="destructive">
                        <AlertCircle aria-hidden="true" />
                        <div>
                          <AlertTitle>Couldn't refresh subscriptions</AlertTitle>
                          <AlertDescription>{humanizeError(subscriptionsError)}</AlertDescription>
                        </div>
                      </Alert>
                    ) : null}

                    {subscriptions.length === 0 ? (
                      <EmptyState
                        icon={Rss}
                        title="No subscriptions"
                        description="Subscriptions created with this app client's credentials will appear here."
                      />
                    ) : (
                      <div className="space-y-2">
                        {subscriptions.map((sub) => (
                          <div key={sub.id} className="rounded-md border bg-card p-4 text-sm">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{sub.name}</p>
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                  Queue:{' '}
                                  <span className="font-mono">{sub.queueName ?? EMPTY_VALUE}</span>
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <StatusBadge status={sub.status} />
                                {canDeleteSubscriptions ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Delete subscription ${sub.name}`}
                                    onClick={() => setPendingSubscriptionDelete(sub)}
                                  >
                                    <Trash2 aria-hidden="true" />
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              <span>Activated: {formatDateTime(sub.activatedAt)}</span>
                              <span>Sensors: {formatNumber(sub.boundSensorCount)}</span>
                            </div>
                            {/* Why a subscription is in error: copy keyed on the API's code,
                                never the worker's or the broker's text (the API does not send
                                it). A persisted state, already there when the dialog opens,
                                so a tinted block without role="alert" — StatusBadge already
                                says Error. */}
                            {sub.status === 'error' ? (() => {
                              const copy = describeSubscriptionError(sub.lastErrorCode);
                              return (
                                <div className="mt-3 flex gap-2 rounded-md border border-destructive-border bg-destructive-subtle p-3 text-destructive-emphasis">
                                  <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                                  <div className="min-w-0 flex-1 space-y-1">
                                    <p className="text-sm font-medium">{copy.title}</p>
                                    <p className="text-xs">{copy.body}</p>
                                    {copy.quoteId ? (
                                      <CredentialField label="Subscription ID" value={sub.id} className="pt-1" />
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })() : null}
                            {sub.status !== 'error' && sub.coverage
                              && Object.values(sub.coverage.event_types).some((t) => !t.covered) ? (
                                <div className="mt-2 text-xs text-muted-foreground">
                                  <p className="font-medium text-foreground">Not covered</p>
                                  <ul className="mt-1 space-y-0.5">
                                    {Object.entries(sub.coverage.event_types)
                                      .filter(([, t]) => !t.covered)
                                      .map(([type, t]) => {
                                        const reason = describeCoverageReason(t.reason);
                                        const canOpen = reason.fix !== null && Boolean(coverageLinks[reason.fix]);
                                        return (
                                          <li key={type}>
                                            <span className="font-mono text-foreground">{type}</span>: {reason.text}
                                            {/* The code support and the SDK see, secondary to the sentence. */}
                                            {t.reason ? (
                                              <span className="ml-1 font-mono text-muted-foreground">{t.reason}</span>
                                            ) : null}
                                            {canOpen && reason.fix ? (
                                              <>
                                                {' · '}
                                                <Link
                                                  to={reason.fix === 'lenses' ? '/lenses' : '/sensors'}
                                                  onClick={onClose}
                                                  className="font-medium text-primary underline-offset-4 hover:underline"
                                                >
                                                  {reason.fix === 'lenses' ? 'Open Lenses' : 'Open Sensors'}
                                                </Link>
                                              </>
                                            ) : null}
                                          </li>
                                        );
                                      })}
                                  </ul>
                                </div>
                              ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
              </div>

            {/*
              The save bar belongs to the form, so it rides with Details, and
              exists only while the name differs from what the server last
              confirmed (useRecordChanges). It stays on Details because the
              rename's error shows under the name field. Renaming changes the
              display name and nothing else; the client id and secret are
              unaffected, which is why they sit right beside it rather than
              behind a warning.
            */}
            {onRename && section === 'details' ? (
              <SaveBar
                changeCount={nameChangeCount}
                onDiscard={discardName}
                onSave={() => {
                  void saveName();
                }}
                isSaving={isSavingName}
              />
            ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {guard.guardDialog}

      <ConfirmDialog
        open={deleteDialogOpen}
        title={`Delete app client "${app.name}"?`}
        description={
          <>
            Integrations that authenticate with its client ID and client secret stop working, and
            its credentials are revoked. This can&apos;t be undone.
          </>
        }
        confirmLabel="Delete app client"
        variant="destructive"
        typeToConfirm={app.name}
        errorContext="Couldn't delete the app client"
        onConfirm={async () => {
          try {
            await onDelete();
          } catch (err: unknown) {
            // ConfirmDialog keeps the refusal inline ("…still has active
            // subscriptions…"); remember the count so closing it leads there.
            const count = appClientSubscriptionBlocker(err);
            if (count !== null) setRaceBlockerCount(count);
            throw err;
          }
          onClose();
        }}
        onClose={() => {
          setDeleteDialogOpen(false);
          if (raceBlockerCount !== null) setSection('subscriptions');
        }}
      />

      {/*
        The escape hatch for a subscription whose credentials are gone. Deleting
        an app client is refused while it still owns subscriptions, and the SDK's
        unsubscribe needs that client's secret — so without this, a lost secret
        left the subscription and its processors running with no way to stop
        them. The API has always permitted it for a user holding
        `subscriptions:delete`; only the button was missing.
      */}
      <ConfirmDialog
        open={pendingSubscriptionDelete !== null}
        title={`Delete subscription "${pendingSubscriptionDelete?.name ?? ''}"?`}
        description={
          <>
            This stops the subscription, releases the processors nothing else is using, and tears
            down its queue. Any consumer still reading that queue stops receiving events. This
            can&apos;t be undone.
          </>
        }
        confirmLabel="Delete subscription"
        variant="destructive"
        errorContext="Couldn't delete the subscription"
        // No try/catch: a failure must reach ConfirmDialog, which keeps the
        // dialog open with the error inline. Catching it here closed the dialog
        // over a subscription that was still running.
        onConfirm={async () => {
          const target = pendingSubscriptionDelete;
          if (!target) return;
          await apiService.deleteSubscription(target.id, accessToken);
          toast.success(`Subscription "${target.name}" deleted`);
          await fetchSubscriptions();
        }}
        onClose={() => setPendingSubscriptionDelete(null)}
      />
    </>
  );
};

export default AppDetailsModal;
